import { Refresh } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useMemo } from "react";
import { useFragment } from "react-relay";
import { InternBrandScrapeAndSaveButton_brand$key } from "./__generated__/InternBrandScrapeAndSaveButton_brand.graphql";
import ButtonWithTooltip from "../../../components/ButtonWithTooltip";
import useScrapeAndSaveBrandStyleMutation from "../../../mutations/useScrapeAndSaveBrandStyleMutation";

const brandFragment = graphql`
  fragment InternBrandScrapeAndSaveButton_brand on BrandType {
    id
    appConfig {
      id
      appState
      appLaunchDate
    }
  }
`;

type InternBrandScrapeAndSaveButtonProps = {
  brand: InternBrandScrapeAndSaveButton_brand$key;
};

const InternBrandScrapeAndSaveButton = ({
  brand: brandKey,
}: InternBrandScrapeAndSaveButtonProps) => {
  const brand = useFragment(brandFragment, brandKey);
  const [scrapeAndSaveMutation, isMutationInFlight] =
    useScrapeAndSaveBrandStyleMutation();

  const handleScrapeAndSave = useCallback(() => {
    scrapeAndSaveMutation(brand.id);
  }, [brand.id, scrapeAndSaveMutation]);

  const disabledTooltip = useMemo(() => {
    if (process.env.NODE_ENV !== "production") {
      return null;
    }
    if (brand.appConfig.appLaunchDate) {
      return "Brand app has already been launched - scrape and save disabled";
    }
    if (
      brand.appConfig.appState !== "PENDING_BRAND_SETUP" &&
      brand.appConfig.appState !== "PENDING_KINN_SETUP"
    ) {
      return "Brand is not in a setup state - scrape and save disabled";
    }
    return null;
  }, [brand.appConfig.appState, brand.appConfig.appLaunchDate]);

  return (
    <ButtonWithTooltip
      tooltip={disabledTooltip}
      variant="contained"
      color="primary"
      startIcon={
        isMutationInFlight ? <CircularProgress size={20} /> : <Refresh />
      }
      onClick={handleScrapeAndSave}
      disabled={isMutationInFlight || !!disabledTooltip}
      sx={{
        minWidth: 200,
      }}
    >
      {isMutationInFlight ? "Scraping..." : "Scrape & Save Brand Style"}
    </ButtonWithTooltip>
  );
};

export default InternBrandScrapeAndSaveButton;
